import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
type Volume = {
  number: string;
  title: string;
  subtitle: string;
  problem: string;
  bullets: string[];
  result: string;
};
const volumes: Volume[] = [{
  number: 'Volume 1',
  title: 'Ballet Foundations',
  subtitle: 'The technical base every student needs to begin with confidence and safety.',
  problem: 'Teaching beginners is rewarding and demanding. This volume gives you the structure to guide brand-new dancers with clarity, patience, and a logical progression.',
  bullets: ['Foundational positions, posture, and body alignment', 'Pliés, tendus, relevés, demi-pointe, and simple barre sequences', 'Port de bras and arm-leg coordination', 'Musical counting and ready-to-use beginner corrections', 'Mini-assessments to see early progress'],
  result: 'Your beginners feel supported, capable, and motivated to return from their very first class.'
}, {
  number: 'Volume 2',
  title: 'Barre Work System',
  subtitle: 'Structured barre sequences—organized, progressive, and ready to teach.',
  problem: 'Barre work is the foundation of every ballet class, but building fresh progressions every week is exhausting. This system keeps each class intentional and connected to the last.',
  bullets: ['Pliés, tendus, dégagés, rond de jambe, fondu, frappé, and adagio', 'Grand battement and relevé progressions', 'Footwork, ankle stability, rotation, and hip control', 'Complete combinations with teacher cues', 'Weekly progressions, transitions, and common-error corrections'],
  result: 'Your barre work feels technically sound and progressive—and students build real strength and precision week after week.'
}, {
  number: 'Volume 3',
  title: 'Center Practice & Balance',
  subtitle: 'Movement away from the barre—fully guided from first step to final pose.',
  problem: 'Center work needs precise sequencing, clear cueing, and spatial awareness. This volume removes the planning burden with center classes that flow beautifully.',
  bullets: ['Center posture, axis control, and port de bras', 'Temps lié, basic adagio, and balance work', 'Preparatory turns and directional changes', 'Musicality and movement fluidity', 'Short center combinations and technical corrections'],
  result: 'Your center classes feel polished, intentional, and deeply satisfying every single time.'
}, {
  number: 'Volume 4',
  title: 'Across-the-Floor & Traveling Steps',
  subtitle: 'The sequences students love most—and the hardest to organize well.',
  problem: 'Across-the-floor work requires musical coordination, spatial organization, and clear progression. These ready-to-teach combinations mean you never have to improvise diagonals again.',
  bullets: ['Ballet walks, chassé, pas de bourrée, glissade, and balancé', 'Basic sautés and petit allegro introduction', 'Simple and more complex diagonals', 'Arm coordination and musical phrasing', 'Entries, exits, transitions, and level variations'],
  result: 'Your traveling sequences feel organized, dynamic, and musically alive—and dancers build real spatial confidence.'
}, {
  number: 'Volume 5',
  title: 'Jumps, Turns & Strength',
  subtitle: 'The technical work students need most—built for safety and progression.',
  problem: 'Jumps and turns are the moments students look forward to most, and the ones that need the most careful preparation. This volume creates a safe pathway for every level.',
  bullets: ['Jump preparation, plié use, and safe landing technique', 'Sautés, changements, échappés, assemblés, and jetés', 'Pirouette preparation and spotting', 'Leg, foot, core, and ankle stability work', 'Safety corrections and error-prevention guides'],
  result: 'Your students develop the physical foundation and confidence to jump and turn well—with complete clarity and safety.'
}, {
  number: 'Volume 6',
  title: 'Complete Ballet Lesson Plans',
  subtitle: 'Full classes ready for every level, age, and teaching context.',
  problem: 'Mixed groups, emergency sessions, recital preparation, and short classes are often the hardest moments to plan. This volume ensures you always have the right session for the room.',
  bullets: ['Beginner, basic, and intermediate lesson plans', 'Posture, barre, center, diagonals, and musicality classes', 'Emergency, time-limited, and full-studio sessions', 'Mixed-ability, review, correction, and assessment classes', 'Recital preparation options'],
  result: 'You have the right class for every teaching moment—without building anything from scratch.'
}];
export function TomosSection() {
  return <section className="bg-ballet-canvas px-4 py-20 sm:py-28" data-id="element-258">
      <div className="mx-auto max-w-6xl" data-id="element-259">
        <div className="mx-auto max-w-3xl text-center" data-id="element-260">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-ballet-rose" data-id="element-261">
            The teaching library
          </p>
          <h2 className="mt-3 font-display text-5xl font-semibold text-ballet-ink" data-id="element-262">
            6 Complete Volumes of Ready-to-Teach Ballet Classes
          </h2>
          <p className="mt-4 text-base leading-7 text-ballet-muted" data-id="element-263">
            Organized by technical objective · Every level covered · Nothing to
            build from scratch
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-264">
          {volumes.map((volume, index) => <motion.article key={volume.number} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.1
        }} transition={{
          delay: index % 2 * 0.08
        }} className="flex h-full flex-col rounded-2xl border border-ballet-line bg-white p-6 shadow-card sm:p-8" data-id="element-265">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-ballet-gold" data-id="element-266">
                {volume.number}
              </p>
              <h3 className="mt-2 font-display text-3xl font-semibold text-ballet-ink" data-id="element-267">
                {volume.title}
              </h3>
              <p className="mt-3 border-l-2 border-ballet-pink pl-4 text-sm font-medium leading-6 text-ballet-rose" data-id="element-268">
                {volume.subtitle}
              </p>
              <div className="mt-6" data-id="element-269">
                <h4 className="text-xs font-bold uppercase tracking-[0.13em] text-ballet-muted" data-id="element-270">
                  What does it solve in your teaching?
                </h4>
                <p className="mt-2 text-sm leading-6 text-ballet-muted" data-id="element-271">
                  {volume.problem}
                </p>
              </div>
              <ul className="mt-6 space-y-2.5" data-id="element-272">
                {volume.bullets.map(bullet => <li key={bullet} className="flex gap-2.5 text-sm leading-6 text-ballet-muted" data-id="element-273">
                    <CheckCircle2Icon size={17} className="mt-0.5 shrink-0 text-ballet-gold" data-id="element-274" />
                    {bullet}
                  </li>)}
              </ul>
              <div className="mt-7 rounded-xl border border-ballet-gold/45 bg-ballet-goldSoft/45 p-4 text-sm leading-6 text-ballet-ink" data-id="element-275">
                <strong className="font-semibold text-ballet-rose" data-id="element-276">
                  Result:{' '}
                </strong>
                {volume.result}
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}